import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getNotifications, markAllNotificationsRead, markNotificationRead } from "../lib/api";
import type { AppNotification } from "../types";

const POLL_INTERVAL_MS = 30000;

function formatTimeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "ahora";
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.floor(hours / 24);
  return `hace ${days} d`;
}

export function NotificationsDropdown({ trigger }: { trigger: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<AppNotification[]>([]);
  const [unread, setUnread] = useState(0);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  async function refresh() {
    try {
      const data = await getNotifications();
      setItems(data.items);
      setUnread(data.unread);
    } catch {
      // ignore
    }
  }

  useEffect(() => {
    refresh();
    const id = window.setInterval(refresh, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (!open) return;
    function handleClick(event: MouseEvent) {
      if (!wrapperRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  async function handleItemClick(item: AppNotification) {
    if (!item.read) {
      setItems((current) =>
        current.map((n) => (n.id === item.id ? { ...n, read: true } : n)),
      );
      setUnread((current) => Math.max(0, current - 1));
      try {
        await markNotificationRead(item.id);
      } catch {
        // ignore
      }
    }
    if (item.link) {
      setOpen(false);
      navigate(item.link);
    }
  }

  async function handleMarkAll() {
    setItems((current) => current.map((n) => ({ ...n, read: true })));
    setUnread(0);
    try {
      await markAllNotificationsRead();
    } catch {
      // ignore
    }
  }

  return (
    <div ref={wrapperRef} className="notif-wrapper">
      <button
        type="button"
        className="sh2-icon-btn sh2-bell-btn"
        aria-label="Notificaciones"
        onClick={() => setOpen((v) => !v)}
      >
        {trigger}
        {unread > 0 ? <span className="sh2-bell-badge">{unread > 99 ? "99+" : unread}</span> : null}
      </button>

      {open ? (
        <div className="notif-dropdown" role="menu">
          <div className="notif-dropdown-head">
            <strong>Notificaciones</strong>
            {unread > 0 ? (
              <button type="button" className="notif-mark-all" onClick={handleMarkAll}>
                Marcar todo como leido
              </button>
            ) : null}
          </div>

          {items.length === 0 ? (
            <div className="notif-empty">No tienes notificaciones.</div>
          ) : (
            <ul className="notif-list">
              {items.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    className={`notif-item${item.read ? "" : " notif-item--unread"}`}
                    onClick={() => handleItemClick(item)}
                  >
                    <span className={`notif-dot notif-dot--${item.type}`} aria-hidden="true" />
                    <div className="notif-item-body">
                      <strong>{item.title}</strong>
                      <p>{item.body}</p>
                      <span>{formatTimeAgo(item.createdAt)}</span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
